<script lang="ts">
	import image8 from "@assets/images/image8.png?webp";
	import image9 from "@assets/images/image9.png?webp";
	import image10 from "@assets/images/image10.png?webp";
	import BuyCampfireLink from "@components/BuyCampfireLink.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Media from "@components/common/Media.svelte";
	import { getAboutPath } from "@core/schema/paths/getAboutPath.js";
	import { getIdFromPath } from "@core/schema/utils/getIdFromPath.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";

	const groups = [
		{
			title: "Cestovatelé a kempaři",
			subtitle: "Kde si večer rozděláš oheň když nesmíš kopat jámu?",
			image: image8,
			content: `
				Vyrážíš do přírody, protože ti dává prostor dýchat. Pod stanem i v karavanu jsou večery plné hvězd. Ale právě večer by to chtělo oheň, teplo, chvíli ticha. Jenže v kempu nebo u jezera ti klasické ohniště nepovolí. A ty nechceš riskovat ani zanechávat spálenou zem.
				nl[[]] nl[[]]
			Přenosné ohniště ti dává možnost rozdělat oheň kdekoliv. Bez kopání, bez poškození okolí. Rozložíš ho do minuty a bez nářadí. Vyrobeno z kvalitní oceli, která zvládne roky cestování. Popel zůstane uvnitř, země zůstane zelená. Konečně máš oheň, co tě nezdržuje a vejde se do kufru tvého auta.
			`,
		},

		{
			title: "Rybáři nl[[]] a vodáci",
			subtitle: "Kam s ohněm, když víš, že příroda není kulisa, ale domov? ",
			image: image10,
			content: `
				Vstáváš dřív než slunce, sbalíš pruty, pádla, kytaru a jedeš tam, kde je klid. Na břeh k vodě. Nepotřebuješ moc. Jen místo, ticho a možná teplý čaj po ránu nebo oheň k večerní pohodě. A víš, že nechceš po sobě zanechat spálenou zem. Jen atmosféru dětství u ohně a přátelé.
				nl[[]] nl[[]]
				Přenosné ohniště postavíš i tam, kde klasický oheň nedává smysl. Nespálí zem, popel zůstane uvnitř, nikde žádný nepořádek. Můžeš si ugrilovat večeři, ohřát ruce i čaj, chvíli jen sedět a koukat. A přesně s tímhle ti přenosné ohniště pomůže
			`,
		},
		{
			title: "Zahrádkáři, chataři a rodiny",
			subtitle: "Jedno ohniště na zahradu, výlet i další dobrodružství? ",
			image: image9,
			content: `
				Máte rádi svůj klid. Zahradu, kterou jste si vytvořili podle sebe. Místo, kde trávíte čas s rodinou, přáteli, dětmi. A večer? Večer by to chtělo oheň. Vrátit se tam, kde se vypráví příběhy. Přenosné ohniště rozložíte během chvilky a postavíte přesně tam, kde ho zrovna potřebujete. Na trávu, na kámen, doprostřed terasy. A když se chystáte na výlet, oslavu nebo návštěvu k přátelům, vezmete ho prostě s sebou. Jedno ohniště, které putuje s vámi.
				nl[[]] nl[[]]
				Popel zůstane uvnitř, povrch pod ním se nezničí. Ráno složíte, uklidíte a zahrada vypadá stejně jako předtím. Jen o zážitek bohatší. Dopřejte si ten pocit, když kolem plamenů sedí všichni, na kterých záleží.
			`,
		},
	];
	let activeIndex = $state(0);

	const activeGroup = $derived.by(() => {
		const activeGroup = groups[activeIndex];

		if (activeGroup === undefined) {
			throw new Error("Active group is undefined");
		}

		return activeGroup;
	});

	let activeGroupContainer = $state<HTMLDivElement>();
	const { tenant } = getGlobalTemplateProps();
</script>

<section>
	<h2 class="text-center max-w-[53.5rem] font-bold mb-10 mx-auto" id={getIdFromPath(getAboutPath(tenant.locale))}>
		Pro koho je Přenosné ohniště od Zažehnuto určené
	</h2>
	<div class="mx-auto grid w-fit max-w-[72rem] grid-cols-1 lg:grid-cols-3 mb-8 gap-2 lg:gap-6">
		{#each groups as group, index (group)}
			<button
				class={[
					activeIndex === index
						? "bg-white border-nov-red-300 text-nov-red-300 border-[3px]"
						: "bg-nov-light-100 [&:not(:hover)]:[--sx:0px] hover:border-nov-red-300 hover:text-nov-red-300 hover:border-[3px]",
					"flex gap-4 xs:gap-6 border rounded-2xl lg:rounded-3xl text-nov-dark-900 items-center text-left ",
					"[--p:2.5] xs:[--p:6] p-[calc(var(--p)*0.25rem-var(--sx,2px))]",
				]}
				onclick={(): void => {
					activeIndex = index;
					activeGroupContainer?.scrollIntoView({ behavior: "smooth", block: "start" });
				}}
			>
				<Media
					wrapperClass="shrink-0 size-20 xs:size-22"
					class="rounded-lg size-full object-cover"
					source={{ src: group.image, type: "image/webp" }}
					height={100}
					width={100}
					alt={group.title}
				/>
				<span class="text-5/5.5 xs:text-title3 font-semibold [&_br]:hidden lg:[&_br]:block">
					<Formatted text={group.title} />
				</span>
			</button>
		{/each}
	</div>
	<div
		bind:this={activeGroupContainer}
		class="flex flex-col p-4 gap-6 lg:gap-4 xs:p-8 xl:gap-x-17 bg-nov-light-100 border border-nov-light-200 rounded-2xl lg:rounded-3xl xl:flex-row"
	>
		<Media
			wrapperClass="w-65 xs:w-auto mx-auto xl:shrink-0 xl:basis-1/2 2xl:basis-159 order-2 lg:order-1"
			class="rounded-lg object-cover size-full"
			source={{ src: activeGroup.image, type: "image/webp" }}
			height={636}
			width={636}
			alt={activeGroup.title}
		/>
		<div class="flex flex-col justify-center order-1 lg:order-2">
			<h2 class="h2 font-bold mb-4 text-center xl:text-left">
				<Formatted text={activeGroup.title} />
			</h2>
			<p class="h3 font-normal !text-nov-red-300 mb-6 xs:mb-10 text-center xl:text-left">
				<Formatted text={activeGroup.subtitle} />
			</p>
			<p class="lg:tracking-[0.005em] text-nov-dark-800 text-center xl:text-left">
				<Formatted text={activeGroup.content} />
			</p>
		</div>
	</div>
	<BuyCampfireLink class="mx-auto mt-10" />
</section>
